// Deployment configuration supplied at build time.
//
// Directory URLs are compiled in because they are the only foothold a fresh install
// has: before the client fetches anything, it has nowhere to get a domain list from.
// From the first successful fetch onwards the list inside the signed manifest wins,
// so these values matter only for the very first contact.

function envString(name: string): string {
  const value = (import.meta.env as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : '';
}

function splitList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const config = {
  /** Protocol version this app speaks — the `X-Imp-Version` header on every request. */
  protocolVersion: 1,

  /** Directory base URLs. Empty means "no directory configured": adding a company is
   *  then impossible, and the screens say so plainly instead of pretending the network
   *  failed. The client treats this as a LIST — random start, next on failure — even
   *  while it holds one entry. */
  directoryUrls: splitList(envString('VITE_DIRECTORY_URLS')),

  /** Public keys allowed to sign the first manifest (base64url, no padding).
   *  Fingerprints are computed, not configured. */
  bootstrapKeys: splitList(envString('VITE_DIRECTORY_KEYS')),

  /** Signature threshold before the app has learned one from a manifest of its own. */
  bootstrapThreshold: Number.parseInt(envString('VITE_DIRECTORY_THRESHOLD') || '2', 10),

  /** Clock tolerance when the manifest carries no `clock_tolerance_s`. */
  defaultClockToleranceS: 7200,

  /** Manifest refresh cycle — decides how quickly a withdrawn entry takes effect. */
  directoryRefreshMs: 6 * 60 * 60 * 1000,

  /** Foreground fallback poll. Alongside web push it is the safety net; without a
   *  push subscription, this and the held request in an open conversation are the
   *  only ways news arrives — the spec'd fallback mode. */
  pollingIntervalMs: 15 * 60 * 1000,

  /** How long the server may hold an events request open (seconds). */
  eventHoldS: 25,

  /** How long a locally held directory entry stays fresh. Entering a conversation
   *  re-checks the contact against the directory no more often than this — the
   *  throttle that keeps routine opens from becoming a live query per contact. */
  directoryEntryMaxAgeMs: 30 * 60 * 1000,

  /** Give up an outbox item after this many attempts. */
  maxSendAttempts: 12,

  /** Local cache of downloaded media, capped by least-recent use. */
  mediaCacheMaxBytes: 50 * 1024 * 1024,
} as const;

export const directoryConfigured = (): boolean => config.directoryUrls.length > 0;
